import { For, ParentProps, Show, Signal, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js'
import styles from './st-camera-input.module.css'
import Button from '../st-button/st-button';
import { Base64String } from 'common';
import Select from '../st-select/st-select';
import { showDialog } from '../st-modal/st-modal';

export class CameraInputProps {
    value?: Signal<Base64String>;
    aspectRatio?: number;
}

interface Bounds {
    width: number;
    height: number;
    left: number;
    top: number;
}

export default function CameraInput(props: ParentProps<CameraInputProps>) {
    let videoEl!: HTMLVideoElement;
    let imgEl!: HTMLImageElement;
    let stream: MediaStream | undefined;

    const aspectRatio = createMemo(() => props.aspectRatio ?? 4 / 3);
    const height = 500;
    const width = 500;

    
    const [value, setValue] = props.value ?? createSignal<Base64String>('');
    const [devices, setDevices] = createSignal<MediaDeviceInfo[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = createSignal<string>('');
    
    const [imageBounds, setImageBounds] = createSignal<Bounds>({ width: 0, height: 0, left: 0, top: 0 });
    const [isCropPanning, setIsCropPanning] = createSignal(false);
    const [isCropScaling, setIsCropScaling] = createSignal(false);
    const [cropBounds, setCropBounds] = createSignal<Bounds>({ width: 0, height: 0, left: 0, top: 0 });
    const [errorMessage, setErrorMessage] = createSignal<string | undefined>(undefined);


    const [handleLocation, setHandleLocation] = createSignal<HandleLocation | undefined>(undefined);

    const selectedDevice = createMemo(() => devices().find(device => device.deviceId === selectedDeviceId()));

    createEffect(() => {
        if (selectedDevice()) {
            stopStream();
            startStream();
        }
    })

    const getMediaApi = () => {
        if (!navigator.mediaDevices) {
            if (location.protocol === 'http:') {
                setErrorMessage('The camera input is only available on secure connections (https).');
            } else {
                setErrorMessage('Your browser does not support the camera input feature.');
            }

            return undefined
        }

        return navigator.mediaDevices
    }

    const startStream = async () => {
        stream = await getMediaApi()?.getUserMedia({ video: { deviceId: selectedDeviceId() } });
        if (stream) {
            videoEl.srcObject = stream;
            videoEl.play();
        }
    }

    const pollDevices = async () => {
        const devices = await getMediaApi()?.enumerateDevices() ?? [];
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setDevices(videoDevices);

        if (videoDevices.length > 0 && !selectedDeviceId()) {
            setSelectedDeviceId(videoDevices[0].deviceId);
        }
    }

    const stopStream = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
    }

    const reset = () => {
        stopStream();
        setValue('');
        startStream();
    }

    const takePhoto = () => {
        const canvasEl = document.createElement('canvas');
        canvasEl.width = videoEl.videoWidth;
        canvasEl.height = videoEl.videoHeight;
        const context = canvasEl.getContext('2d');
        if (context) {
            context.drawImage(videoEl, 0, 0, videoEl.videoWidth, videoEl.videoHeight);
            const data = canvasEl.toDataURL('image/png');
            setValue(data);
            imgEl.src = data;
            imgEl.onload = () => {
                setImageBounds(calculateImageBounds());

                const cropHeight = imageBounds().height;
                const cropWidth = cropHeight * aspectRatio();

                setCropBounds({
                    width: cropWidth,
                    height: cropHeight,
                    left: (imageBounds().width - cropWidth) / 2,
                    top: 0,
                });
            }
        }

        stopStream();
    }

    const calculateImageBounds = (): Bounds => {
        const containerWidth = imgEl.clientWidth;
        const containerHeight = imgEl.clientHeight;

        const imageAspectRatio = imgEl.naturalWidth / imgEl.naturalHeight;
        const containerAspectRatio = containerWidth / containerHeight;

        const width = imageAspectRatio > containerAspectRatio ? containerWidth : containerHeight * imageAspectRatio;
        const height = imageAspectRatio > containerAspectRatio ? containerWidth / imageAspectRatio : containerHeight;

        return { width, height, left: (containerWidth - width) / 2, top: (containerHeight - height) / 2 }
    }


    onMount(() => {
        pollDevices();
    })

    onCleanup(() => {
        stopStream();
    })

    createEffect(() => {
        if (isCropPanning() || isCropScaling()) {
            document.removeEventListener('mouseup', onMouseup);
            document.addEventListener('mouseup', onMouseup);
        } else {
            document.removeEventListener('mouseup', onMouseup);
        }
    })

    createEffect(() => {
        const cBounds = cropBounds();

        if (cBounds.width === 0 || cBounds.height === 0) {
            return;
        }

        const resolvedX = cBounds.left / imageBounds().width * imgEl.naturalWidth;
        const resolvedY = cBounds.top / imageBounds().height * imgEl.naturalHeight;
        const resolvedWidth = cBounds.width / imageBounds().width * imgEl.naturalWidth;
        const resolvedHeight = cBounds.height / imageBounds().height * imgEl.naturalHeight;
        
        const canvas = document.createElement('canvas');
        canvas.width = resolvedWidth;
        canvas.height = resolvedHeight;
        const context = canvas.getContext('2d');
        if (context) {
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(imgEl, resolvedX, resolvedY, resolvedWidth, resolvedHeight, 0, 0, canvas.width, canvas.height);
            const data = canvas.toDataURL('image/png');
            setValue(data);
        }
    })

    const onMouseup = () => {
        setIsCropPanning(false);
        setIsCropScaling(false);
    }

    const beginPanCrop = () => {
        setIsCropPanning(true);
        document.addEventListener('mouseup', onMouseup);
        document.addEventListener('mousemove', panCrop);
    }

    const beginScaleCrop = (i: HandleLocation) => {
        setIsCropScaling(true);
        setHandleLocation(i);
        document.addEventListener('mouseup', onMouseup);
        document.addEventListener('mousemove', scaleCrop);
    }

    const panCrop = (e: MouseEvent) => {
        if (isCropPanning()) {
            const cBounds = cropBounds();
            const newX = Math.max(0, Math.min(imageBounds().width - cBounds.width, cBounds.left + e.movementX));
            const newY = Math.max(0, Math.min(imageBounds().height - cBounds.height, cBounds.top + e.movementY));

            setCropBounds({
                ...cBounds,
                left: newX,
                top: newY,
            })
        }
    }

    const scaleCrop = (e: MouseEvent) => {
        const location = handleLocation();
        if (isCropScaling() && location !== undefined) {
            const dx = e.movementX;
            const dy = e.movementY;

            let newCrop: Bounds = { ...cropBounds() };

            switch (location) {
                case HandleLocation.Top:
                case HandleLocation.Bottom:
                    newCrop.top += location === HandleLocation.Top ? dy : 0;
                    newCrop.height += location === HandleLocation.Top ? -dy : dy;

                    if (newCrop.top < 0) {
                        newCrop.height -= newCrop.top;
                        newCrop.top = 0;
                    }

                    if (newCrop.top + newCrop.height > imageBounds().height) {
                        newCrop.top = imageBounds().height - newCrop.height;

                        if (newCrop.top < 0) {
                            newCrop.height += newCrop.top;
                            newCrop.top = 0;
                        }
                    }

                    const newWidth = newCrop.height * aspectRatio();
                    newCrop.left += (newCrop.width - newWidth) / 2;
                    newCrop.width = newWidth;
                    break;
                case HandleLocation.Right:
                case HandleLocation.Left:
                    newCrop.left += location === HandleLocation.Left ? dx : 0;
                    newCrop.width += location === HandleLocation.Right ? dx : -dx;

                    if (newCrop.left < 0) {
                        newCrop.width -= newCrop.left;
                        newCrop.left = 0;
                    }

                    if (newCrop.left + newCrop.width > imageBounds().width) {
                        newCrop.left = imageBounds().width - newCrop.width;

                        if (newCrop.left < 0) {
                            newCrop.width += newCrop.left;
                            newCrop.left = 0;
                        }
                    }

                    const newHeight = newCrop.width / aspectRatio();
                    newCrop.top += (newCrop.height - newHeight) / 2;
                    newCrop.height = newHeight;
                    break;
            }

            setCropBounds(newCrop);
        }
    }

    return (
        <div class={styles['st-camera-input']}>
            <Show when={!errorMessage()} fallback={<div class={styles['error-message']}>{errorMessage()}</div>}>
                <Select value={[selectedDeviceId, setSelectedDeviceId]}>
                    <For each={devices()}>{device => (
                        <Select.Option value={device.deviceId}>{device.label}</Select.Option>
                    )}</For>
                </Select>

                <div class={styles['preview-area']} style='position: relative;'>
                        <Show when={!value()}>
                            <video ref={videoEl} width={width} height={height}>Video stream not available.</video>
                        </Show>
                        <Show when={value()}>
                            <div class={styles['overlay']} style={{ width: `${imageBounds().width}px`, height: `${imageBounds().height}px`, top: `${imageBounds().top}px`, left: `${imageBounds().left}px` }}>
                                <div
                                    class={styles['frame']}
                                    style={{ width: `${cropBounds().width}px`, height: `${cropBounds().height}px`, top: `${cropBounds().top}px`, left: `${cropBounds().left}px` }}
                                    onMouseDown={() => beginPanCrop()}
                                />
                                <div class={styles['handle-container']} style={{ width: `${cropBounds().width}px`, height: `${cropBounds().height}px`, top: `${cropBounds().top}px`, left: `${cropBounds().left}px` }}>
                                    <div onMouseDown={() => beginScaleCrop(HandleLocation.Top)} class={`${styles['handle']} fa fa-arrows-v`} style={{ top: 0, left: `${cropBounds().width / 2}px`}} />
                                    <div onMouseDown={() => beginScaleCrop(HandleLocation.Right)} class={`${styles['handle']} fa fa-arrows-h`} style={{ top: `${cropBounds().height / 2}px`, left: `${cropBounds().width}px`}} />
                                    <div onMouseDown={() => beginScaleCrop(HandleLocation.Bottom)} class={`${styles['handle']} fa fa-arrows-v`} style={{ top: `${cropBounds().height}px`, left: `${cropBounds().width / 2}px`}} />
                                    <div onMouseDown={() => beginScaleCrop(HandleLocation.Left)} class={`${styles['handle']} fa fa-arrows-h`} style={{ top: `${cropBounds().height / 2}px`, left: 0}} />
                                </div>
                            </div>
                            <img src="Images/ST_REVELATION/NoPhoto.png" ref={imgEl} width={width} height={height} alt="A preview of the image taken by the user."></img>
                        </Show>
                </div>

                <Show when={value()}>
                    <Button icon='fa fa-undo' onClick={() => reset()}>Retake</Button>
                </Show>

                <Show when={!value()}>
                    <Button icon='fa fa-camera' onClick={() => takePhoto()}>Take photo</Button>
                </Show>
            </Show>
        </div>
    );
}

enum HandleLocation {
    Top,
    Right,
    Bottom,
    Left,
}

CameraInput.defaultProps = new CameraInputProps();

type RequestWebcamPhotoConfig = Exclude<CameraInputProps, 'value'>;

export const requestWebcamPhoto = async (config: RequestWebcamPhotoConfig) => {
    const base64Img = await showDialog<Base64String>((resolve, reject) => {
        const [value, setValue] = createSignal<Base64String>('');

        return (
            <div style='display: flex; flex-direction: column; align-items: end; gap: 1em;'>
                <CameraInput value={[value, setValue]} {...config}></CameraInput>
                <Button enabled={!!value()} type='primary' onClick={() => resolve(value())}>Save</Button>
            </div>
        )
    }, { title: 'Take a photo' });

    return base64Img;
}
